import auth from '@lib/plugins/auth';
import { extensionMenu } from '@lib/extensions';
import { createSidebar } from '@lib/plugins/sidebar';
import { useAppContextStore } from '@lib/plugins/layouts/context';

import type { SidebarItemLinkType, SidebarItemRouterType, SidebarItemType } from '@lib/stores/app-sidebar-menu';
import type { AuthUser } from '@lib/types';

type AppContextStore = ReturnType<typeof useAppContextStore>;

/** What a group can hold: pages and links, no dividers or headers. */
function pageEntries(items: Array<SidebarItemType>): Array<SidebarItemRouterType | SidebarItemLinkType> {
	return items.filter((item): item is SidebarItemRouterType | SidebarItemLinkType => 'to' in item || 'href' in item);
}

/**
 * The menu of the app; the lib keeps it in sync with the user and the active event and drops
 * the entries whose `permissions` the user lacks (lib/docs/components/sidebar.md).
 */
function buildSidebarMenu(user: AuthUser, appContext: AppContextStore): Array<SidebarItemType> {
	const event = appContext.getEvent;

	// The settings of the active event, by the event's read guard as their tabs. A guard of the
	// event, not a permission, so it is decided here rather than by `permissions`.
	const canReadSettings = !!event && (appContext.can('event', 'event.read') || auth.check('events.read'));

	// The active event under its name: its records, for everyone who takes part in it, and its settings
	const eventGroup: Array<SidebarItemType> =
		event ?
			[
				{ divider: true },
				{
					label: 'event_header',
					title: event.name,
					collapsible: false,
					children: [
						{ to: '/bands', icon: 'fa fa-users', label: 'bands' },
						{ to: '/stages', icon: 'fa fa-star', label: 'stages' },
						{ to: '/stage_plan_items', icon: 'fa fa-vector-square', label: 'stage_plan_items' },
						{ to: '/timeslots', icon: 'fa fa-clock', label: 'timeslots' },
						{ to: '/shifts', icon: 'fa fa-clipboard-list', label: 'shifts' },
						{ to: '/stage_manager_shifts', icon: 'fa fa-clipboard-user', label: 'stage_manager_shifts' },
						{ to: '/notifications', icon: 'fa fa-commenting', label: 'stage_notifications' },
						...(canReadSettings ?
							[
								{
									to: `/events/${event.id}/settings`,
									icon: ['fa-cog', 'fa-campground'] as [string, string],
									label: 'settings',
								},
							]
						:	[]),
					],
				},
			]
		:	[];

	return [
		{ to: '/dashboard', icon: 'fa fa-home', label: 'dashboard' },
		...eventGroup,
		// The extensions' own pages, after the app's
		...extensionMenu('main', user, appContext),

		// Administration; the group goes when the user may see none of its entries
		{ divider: true },
		{
			label: 'admin_header',
			icon: 'fa fa-user-shield',
			children: [
				{ to: '/admin/users', icon: 'fa fa-users', label: 'users', permissions: ['users.read'] },
				{ to: '/admin/events', icon: 'fa fa-campground', label: 'events', permissions: ['events.read'] },
				{ to: '/admin/screens', icon: 'fa fa-desktop', label: 'screens', permissions: ['app.settings'] },
				{ to: '/admin/settings', icon: 'fa fa-cogs', label: 'settings', permissions: ['app.settings'] },
				{
					to: '/admin/maintenance',
					icon: 'fa fa-screwdriver-wrench',
					label: 'maintenance',
					permissions: ['app.developer'],
				},
				// The development page has no entry, it stays at /admin/development
				{
					href: '/docs/swagger',
					icon: 'fa-book',
					label: 'swagger',
					opener: true,
					permissions: ['app.developer'],
				},
				// The extensions' own, e.g. the info page of the MCP server
				...pageEntries(extensionMenu('admin', user, appContext)),
			],
		},
	];
}

export default createSidebar({ menu: buildSidebarMenu });
